import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, ImagePlus, X } from "lucide-react";

const FACILITY_TYPES = ["Hospital", "Clinic", "Diagnostic Centre", "Day Care Centre"];
export const EMPTY_FACILITY = {
  name: "", code: "", facility_type: "Hospital", logo: "", address: "", city: "", state: "", country: "India",
  pincode: "", contact_number: "", email: "", beds: 0, registration_number: "", gst_number: "", pan: "",
};

function Field({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>;
}

export function FacilityDialog({ open, onOpenChange, initial, onSave, title }) {
  const [form, setForm] = useState(EMPTY_FACILITY);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm({ ...EMPTY_FACILITY, ...(initial || {}) }); }, [open, initial]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1024 * 1024) { alert("Please choose a logo under 1 MB."); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, logo: reader.result }));
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    setSaving(true);
    try { await onSave(form); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto thin-scroll" data-testid="facility-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="space-y-5">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-wider text-slate-400 mb-2">Basic Details</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Facility name"><Input value={form.name} onChange={set("name")} data-testid="fac-name-input" /></Field>
              <Field label="Facility code"><Input value={form.code} onChange={set("code")} data-testid="fac-code-input" /></Field>
              <Field label="Facility type">
                <select className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm bg-white" value={form.facility_type} onChange={set("facility_type")} data-testid="fac-type-select">
                  {FACILITY_TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Number of beds"><Input type="number" value={form.beds} onChange={(e) => setForm({ ...form, beds: parseInt(e.target.value || 0) })} data-testid="fac-beds-input" /></Field>
            </div>
          </div>
          <div>
            <div className="text-[11px] font-mono uppercase tracking-wider text-slate-400 mb-2">Logo (appears on prescription & discharge PDFs)</div>
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-md border border-slate-200 bg-slate-50 grid place-items-center overflow-hidden shrink-0">
                {form.logo ? <img src={form.logo} alt="logo" className="h-full w-full object-contain" data-testid="fac-logo-preview" /> : <ImagePlus className="h-5 w-5 text-slate-300" />}
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm px-3 py-1.5 rounded-md border border-slate-300 bg-white cursor-pointer hover:bg-slate-50" data-testid="fac-logo-label">
                  {form.logo ? "Replace logo" : "Upload logo"}
                  <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={onLogo} data-testid="fac-logo-input" />
                </label>
                {form.logo && <Button type="button" variant="ghost" size="sm" className="text-red-500" onClick={() => setForm({ ...form, logo: "" })} data-testid="fac-logo-remove"><X className="h-4 w-4 mr-1" /> Remove</Button>}
              </div>
            </div>
            <p className="text-[11px] text-slate-400 mt-1.5">PNG or JPG, under 1 MB. A square logo works best.</p>
          </div>
          <div>
            <div className="text-[11px] font-mono uppercase tracking-wider text-slate-400 mb-2">Address & Contact</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Address"><Input value={form.address} onChange={set("address")} data-testid="fac-address-input" /></Field>
              <Field label="City"><Input value={form.city} onChange={set("city")} /></Field>
              <Field label="State"><Input value={form.state} onChange={set("state")} /></Field>
              <Field label="Country"><Input value={form.country} onChange={set("country")} /></Field>
              <Field label="PIN code"><Input value={form.pincode} onChange={set("pincode")} /></Field>
              <Field label="Contact number"><Input value={form.contact_number} onChange={set("contact_number")} /></Field>
              <Field label="Email"><Input value={form.email} onChange={set("email")} /></Field>
            </div>
          </div>
          <div>
            <div className="text-[11px] font-mono uppercase tracking-wider text-slate-400 mb-2">Operational & Compliance</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Registration number"><Input value={form.registration_number} onChange={set("registration_number")} /></Field>
              <Field label="GST number"><Input value={form.gst_number} onChange={set("gst_number")} /></Field>
              <Field label="PAN"><Input value={form.pan} onChange={set("pan")} /></Field>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !form.name || !form.code} className="bg-blue-700 hover:bg-blue-800" data-testid="fac-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save facility"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
